/* eslint-disable max-lines */
import { normalizeNumber } from '@salescore/buff-common'
import { Array, Data, Effect, HashSet, identity, Number, Order, pipe, Record, String } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { z } from 'zod'

import { CORE_CONSTANT } from '../../constant.ts'
import type {
  ViewQueryDimensionFieldForMultiTablePivot,
  ViewQueryMultiTablePivot,
  ViewQueryMultiTablePivotQuery,
  ViewQueryNode,
  ViewQueryRecordNode,
  ViewQueryResult,
} from '../../schemas/query/index.ts'
import { assignInnerRowGeometry } from '../util/assignInnerRowGeometry.ts'
import { flatRecordNodes } from '../util/recordNodeUtility.ts'
import { type SqlGenerationError, type SqlOverrideResolverError, wrapErrorOrDie } from './executeViewQuery/errors.ts'
import { generateLabelSqlFieldName, type NodePredicateResolver } from './executeViewQuery/generateSql.ts'
import { runPromiseSquashingCause } from './executeViewQuery/runPromiseSquashingCause.ts'
import { executeAggregationQueryEffect, type SqlClient } from './executeViewQueryForAggregationQuery.ts'

const sqlResultRecordSchema = z.record(z.string(), z.unknown())
type SqlResultRecord = z.infer<typeof sqlResultRecordSchema>

interface UserOrUserGroup {
  id: string
  rank: number
}

interface DimensionSortResources {
  userIds: string[]
  userGroupIds: string[]
}

const kpiQueryResultSchema = z.object({
  rows: sqlResultRecordSchema.array(),
  hasMoreRows: z.boolean().optional(),
  sql: z.string(),
  originalRawSql: z.string(),
  overriddenRawSql: z.string().optional(),
  mustacheParameter: z.record(z.string(), z.unknown()),
  name: z.string(),
  kpiId: z.string().optional(),
  kpiName: z.string().optional(),
  error: z.string().optional(),
})
type KpiQueryResult = z.infer<typeof kpiQueryResultSchema>

/**
 * KPI / ダッシュボード用の ViewQuery を実行します。
 *
 * 流れ:
 *
 * 1. `queries` (= 複数の集計クエリ) を並列実行
 * 2. 各結果に `kpiId` / `kpiName` / `kpiGroupName` / `role` を付与
 * 3. 全集計結果を `flatMap` で 1 つの配列に結合
 * 4. ピボット集計を実行 (`groupByRowDimensions`)
 * 5. 必要に応じてソート (`sortByAggregatedValues`)
 * 6. 内部行ジオメトリ付与
 * 7. データが大きすぎたら削る (`reduceRecordNode`)
 *
 * `kpiId` 付与は「集計結果を 1 つの配列にまとめてからピボットする」設計のため必要です
 * (詳細は packages/common/core/doc/dashboard.md の「集計値のピボット」を参照)。
 */
export const executeViewQueryForMultiTablePivot = async (
  parameters: ExecuteViewQueryForMultiTablePivotParameters,
): Promise<ViewQueryResult> =>
  await runPromiseSquashingCause(
    executeViewQueryForMultiTablePivotEffect(parameters).pipe(
      // アプリ境界: タグを剥がして元エラーに戻し、呼び出し元のinstanceof / message判定の契約を維持する。
      // Effect.catchでなくEffect.mapErrorにしているのは予防のため。Effect.catchはCauseにdefectが混ざるとハンドラを
      // 呼ばずfailureをdefect化し、message空のタグ付きエラーがthrowされる。effect v4のEffect.allは並列の失敗を
      // 先に起きた側だけCauseに残すので、現状のKPIの並列実行では混ざらない。それでもCauseを合成する経路が
      // 加わった場合に、Effect.mapErrorならdefectを残したままfailureの値だけを置き換えられる
      Effect.mapError((error) => error.cause),
    ),
  )

interface ExecuteViewQueryForMultiTablePivotParameters {
  query: ViewQueryMultiTablePivot
  sqlClient: SqlClient
  mustacheParameter: Record<string, unknown>
  unlimitedRowsEnabled: boolean
  accountClosingMonth: number
  sqlOverrideResolver?: (sql: string) => Promise<string | undefined>
  resolveNodePredicate: NodePredicateResolver
  posthogTrack?: (duration: number, query: ViewQueryMultiTablePivotQuery) => void
}

// createViewQueryResult (結果組み立て。内部でfetchUsers / fetchUserGroupsのSQL実行を含む) の失敗。
// 本ファイルのPromise境界で剥がされて外に出ないため、共有タグ群 (executeViewQuery/errors.ts) には置かない
class ViewQueryResultBuildError extends Data.TaggedError('ViewQueryResultBuildError')<{ cause: Error }> {}

const executeViewQueryForMultiTablePivotEffect = ({
  query,
  sqlClient,
  mustacheParameter,
  unlimitedRowsEnabled,
  accountClosingMonth,
  sqlOverrideResolver,
  resolveNodePredicate,
  posthogTrack,
}: ExecuteViewQueryForMultiTablePivotParameters): Effect.Effect<
  ViewQueryResult,
  SqlGenerationError | SqlOverrideResolverError | ViewQueryResultBuildError
> =>
  Effect.gen(function* () {
    const kpiIdToKpiGroupName = Record.fromIterableWith(query.queries, (q) => [
      q.kpiId,
      q.kpiGroupName ?? CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME,
    ])

    // SQL実行エラー (SqlExecutionError) だけをKPI単位のエラー表示に変換して握りつぶし、
    // SQL生成・sqlOverrideResolverの失敗は失敗チャンネルのまま素通しして従来どおり全体の例外として
    // 伝播させる。この非対称な契約は、戻り値の失敗型からSqlExecutionErrorが消えていることで型検査される
    const executeQueryEffect = (
      query: ViewQueryMultiTablePivotQuery,
    ): Effect.Effect<KpiQueryResult, SqlGenerationError | SqlOverrideResolverError> => {
      if (query.schema === null) {
        return Effect.succeed({
          rows: [],
          sql: '',
          originalRawSql: '',
          overriddenRawSql: undefined,
          mustacheParameter: {},
          name: query.name,
        })
      }
      return executeAggregationQueryEffect({
        query: query.schema,
        sqlClient,
        mustacheParameter,
        accountClosingMonth,
        sqlOverrideResolver,
        resolveNodePredicate,
        option: {
          logBody: {
            viewId: query.kpiId,
            viewName: query.kpiName,
          },
          unlimitedRowsEnabled,
        },
      }).pipe(
        Effect.map(({ rows, hasMoreRows, sql, originalRawSql, overriddenRawSql }): KpiQueryResult => {
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: LIMIT
          const rowsWithKpiName = rows.map((row): Record<string, unknown> => ({
            ...row,
            ...(query.role === 'goal'
              ? {
                  // 目標のとき、KPIのIDはレコードに含まれるが、SQLが複雑になるのを避けてKPIグループ名はSQLに付与しないため、ここで処理する
                  [CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_NAME_COLUMN_NAME]:
                    kpiIdToKpiGroupName[
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-unnecessary-condition
                      (row[CORE_CONSTANT.KPI_PIVOT_KPI_ID_COLUMN_NAME] as string) ??
                        CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME
                    ],
                }
              : {
                  [CORE_CONSTANT.KPI_PIVOT_KPI_ID_COLUMN_NAME]: query.kpiId,
                  [generateLabelSqlFieldName(CORE_CONSTANT.KPI_PIVOT_KPI_ID_COLUMN_NAME)]: query.kpiName,
                  [CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_NAME_COLUMN_NAME]:
                    query.kpiGroupName ?? CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME,
                }),
            [CORE_CONSTANT.KPI_PIVOT_ROLE_COLUMN_NAME]: query.role,
          }))

          return {
            rows: rowsWithKpiName,
            hasMoreRows,
            sql,
            originalRawSql,
            overriddenRawSql,
            mustacheParameter,
            name: query.name,
            kpiId: query.kpiId,
            kpiName: query.kpiName,
          }
        }),
        Effect.catchTag(
          'SqlExecutionError',
          (error): Effect.Effect<KpiQueryResult> =>
            Effect.succeed({
              rows: [
                // ここで1レコード分は返さないと、カラムとして表示されない
                createEmptyColumnRow(query),
              ],
              sql: error.sql,
              originalRawSql: error.originalRawSql,
              overriddenRawSql: error.overriddenRawSql,
              mustacheParameter,
              name: query.name,
              kpiId: query.kpiId,
              kpiName: query.kpiName,
              error: `${query.kpiName}のSQL実行中にエラーが発生しました。 ${error.cause.message}`,
              // eslint-disable-next-line sonarjs/todo-tag
            }), // TODO: エラー表示
        ),
      )
    }

    const start = yield* Effect.sync(() => Date.now())

    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: chunk
    const kpiQueryEffects = query.queries.map((query) =>
      executeQueryEffect(query).pipe(
        Effect.tap(() =>
          // eslint-disable-next-line max-nested-callbacks
          Effect.sync(() => {
            posthogTrack?.(Date.now() - start, query)
          }),
        ),
      ),
    )

    // Promise.allと同様に全KPIを並列実行し、最初の失敗 (failure/defect) で全体をfailさせる。
    // Promise.allとの差分 (受容): 失敗時に未完了の兄弟KPIのEffectはinterruptされ、そのKPIのSQL発行と
    // 付随する副作用 (キャッシュ書き込み・posthogTrack計測など) がスキップされうる
    // (Promise.allは reject 後も兄弟promiseが完走し副作用も発火していた)。失敗源はSQL生成・
    // sqlOverrideResolverの失敗のほか、posthogTrackのthrow等のdefectも含む (KPIごとのSQL実行エラーは
    // executeQueryEffectが成功チャンネルのKpiQueryResultに変換済みでfailしない)。いずれの失敗でも
    // レスポンス全体が例外になるリクエスト内に差分が閉じ、レスポンス内容には影響しないため受容する。
    // なおEffect.exitで各KPIを包み完走させる代替案は、Promise.allが最初のrejectで即座に応答するのに
    // 対し全KPI完了までエラー応答が遅延するため、こちらの差分のほうが大きいと判断し採らなかった
    const results = yield* Effect.all(kpiQueryEffects, { concurrency: 'unbounded' })

    return yield* Effect.tryPromise({
      try: async () => await createViewQueryResult({ query, sqlClient, results, unlimitedRowsEnabled }),
      catch: identity,
    }).pipe(
      // createViewQueryResult内のSQL実行 (fetchUsers / fetchUserGroups) 等の失敗はKPI単位エラー化の
      // 対象外で、どの呼び出し元も回復せずリクエスト全体の例外として伝播する従来契約。
      // 想定内のDB失敗なので他経路と同様にタグ付き失敗に分類し、境界のタグ剥がしで元Errorに戻す
      Effect.catch(wrapErrorOrDie((cause) => new ViewQueryResultBuildError({ cause }))),
    )
  })

function createEmptyColumnRow(query: ViewQueryMultiTablePivotQuery) {
  return {
    [CORE_CONSTANT.KPI_PIVOT_KPI_ID_COLUMN_NAME]: query.kpiId,
    [generateLabelSqlFieldName(CORE_CONSTANT.KPI_PIVOT_KPI_ID_COLUMN_NAME)]: query.kpiName,
    [CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_NAME_COLUMN_NAME]: query.kpiGroupName ?? CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME,
    [CORE_CONSTANT.KPI_PIVOT_ROLE_COLUMN_NAME]: query.role,
  }
}

// KPI ピボットの木は列定義ではなく pivot.rows の階層から組まれ、どのブロックも行の高さに寄与する。
// tree は同じ階層から生成され、レコード木に現れるノード名を漏れなく含むため、その全ノード名を可視集合とする。
const collectNodeNames = (node: ViewQueryNode): string[] => [
  node.name,
  ...(node.children ?? []).flatMap((child) => collectNodeNames(child)),
]

async function createViewQueryResult({
  query,
  sqlClient,
  results,
  unlimitedRowsEnabled,
}: {
  query: ViewQueryMultiTablePivot
  results: KpiQueryResult[]
  sqlClient: SqlClient
  unlimitedRowsEnabled: boolean
}): Promise<ViewQueryResult> {
  // XXX: どういうロジックが理想か悩ましいが、ユーザー・ユーザーグループによるソートの実現のため、ここでusers,userGroupsをselectしている
  const users = await fetchUsers(query.pivot, sqlClient)
  const userGroups = await fetchUserGroups(query.pivot, sqlClient)
  const resources: DimensionSortResources = {
    userIds: users.map((x) => x.id),
    userGroupIds: userGroups.map((x) => x.id),
  }

  const allRows = results.flatMap((x) => x.rows) // pivot上の総セル数に相当
  const nodeRecords = groupByRowDimensions(
    allRows,
    query.pivot,
    query.sorter,
    query.options,
    1, // rootのテーブルノードが0なので、1始まりで正しい
    resources,
  )
  const hasTooManyRows = allRows.length > 50 * CORE_CONSTANT.AGGREGATION_QUERY_LIMIT // グルーピング性能の悪化が顕在化する場合、実際に複数KPIのレコード総数に対しLIMITする
  const sorted = sortByAggregatedValues(nodeRecords, query)
  const visibleNodeNames = HashSet.fromIterable(collectNodeNames(query.tree))
  const withInnerIndex = sorted.map((record) => assignInnerRowGeometry(record, visibleNodeNames))
  // eslint-disable-next-line sonarjs/todo-tag
  const reducedResult = reduceRecordNode(withInnerIndex, CORE_CONSTANT.AGGREGATION_ROWS_LIMIT) // TODO: pagination
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const isRowReduced = reducedResult.reduced || results.some((x) => x.hasMoreRows) //  AGGREGATION_ROWS_LIMIT 以上のレコードを持つ KPI が複数ある場合でも、バランスよくダッシュボードを表示するため KPI 毎にも LIMIT している
  return {
    viewQuery: query,
    result: unlimitedRowsEnabled ? withInnerIndex : reducedResult.records,
    sqls: Array.flatMapNullishOr(
      results.map((x) => x.sql),
      identity,
    ), // deprecated
    sqlDetails: results.map((x) => ({
      sql: x.sql,
      originalRawSql: x.originalRawSql,
      overriddenRawSql: x.overriddenRawSql,
      mustacheParameter: x.mustacheParameter,
      name: x.kpiName ?? x.name,
    })),
    nextCursor: undefined,
    error:
      results.length > 0
        ? Array.flatMapNullishOr(
            results.map((x) => x.error),
            identity,
          ).join(', ')
        : undefined,
    warn:
      isRowReduced && !unlimitedRowsEnabled
        ? `行が多すぎるため、全てを表示できません。「集計項目 - 行」を減らすと問題が解消する場合があります。`
        : // eslint-disable-next-line sonarjs/no-nested-conditional
          hasTooManyRows
          ? `レコード(セル数)が多すぎるため、表示に時間がかかったりページが重くなる場合があります。`
          : undefined,
    ui: {
      pivotColumns: query.pivot.columns.map((column) => {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: fixedValues
        const valuesWithLabel = pipe(
          allRows,
          Array.map((row) => ({
            // eslint-disable-next-line sonarjs/todo-tag
            value: forceString(row[column.name]) ?? '', // TODO: nullの扱いどうすべき？
            label:
              row[column.name] === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING
                ? '小計'
                : // eslint-disable-next-line sonarjs/no-nested-conditional
                  row[column.name] === CORE_CONSTANT.KPI_PIVOT_NULL_STRING
                  ? '-'
                  : (forceString(row[generateLabelSqlFieldName(column.name)]) ?? undefined),
          })),
          // eslint-disable-next-line sonarjs/todo-tag
          Array.dedupeWith((a, b) => a.value === b.value), // TODO: sort
          Array.sortBy(
            Order.mapInput(dimensionSortKeyOrder, (row) =>
              buildPivotDimensionSortKey({
                dimension: column,
                dimensionValue: row.value,
                label: row.label ?? row.value,
                isTotalPlacedFirst: query.sorter?.totalColumnOrder === 'left',
                resources,
              }),
            ),
          ),
        )

        return {
          columnFieldName: column.name,
          valuesWithLabel,
        }
      }),
    },
  }
}

function getNodeOrderings(
  keys: Array<{ key: string; order: 'asc' | 'desc' }>,
  node: Record<string, unknown>,
): number[] {
  return keys.map((key) => {
    const value = normalizeNumber(node[key.key]) ?? 0 // XXX: デフォルトを0としている。この挙動をやめたいが、ほとんどのKPIにおいてデフォルトは0であるような挙動が期待されており、client側では0と表示しているので、0としてしまう
    // やや雑だが、以下でasc/descを対応する
    if (key.order === 'desc') {
      return -value
    }
    return value
  })
}

// UIでソート列を指定した場合、集計値によるソート（＝recordNodesをflatした状態でのソート）
// XXX: LIMITがあるケースを考えると、本来はSQL側でのsortが必要になるが、
//      複数テーブルをまたがって特定のテーブルのカラム値でソートするのは非常に難しいため、いったんアプリケーションレイヤーでソートを行う。
function sortByAggregatedValues(
  recordNodes: ViewQueryRecordNode[],
  query: ViewQueryMultiTablePivot,
): ViewQueryRecordNode[] {
  const keys = query.sorter?.columnKeys
  if (keys === undefined || keys.length === 0) {
    return recordNodes
  }

  // 一旦flatにする
  const flatten = recordNodes.flatMap((x) => flatRecordNodes(x, []))
  // 小計を外す
  const notTotalNodes = flatten.filter((x) =>
    pipe(
      x,
      Record.values,
      Array.every((value) => value !== CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING),
    ),
  )
  // sortする
  const sortedNotTotalNodes = pipe(
    notTotalNodes,
    Array.sortBy(Order.mapInput(Order.Array(Order.Number), (x) => getNodeOrderings(keys, x))),
  )

  // KPIの合計セルはソート後に追加する
  const kpiTotalNodes = recordNodes
    .filter((x) => x.id === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING)
    .flatMap((x) => flatRecordNodes(x, []))

  const sortedNodes =
    query.sorter?.totalRowOrder === 'top'
      ? [...kpiTotalNodes, ...sortedNotTotalNodes]
      : [...sortedNotTotalNodes, ...kpiTotalNodes]

  return sortedNodes.map((attributes, index): ViewQueryRecordNode => ({
    id: `${index}`,
    attributes,
    children: [],
    meta: {
      height: 1,
      innerRowIndexStart: 0,
      innerRowIndexEnd: 1,
    },
  }))
}

function forceString(x: unknown): string | null | undefined {
  if (x === undefined || x === null) {
    return x
  }

  if (typeof x === 'string') {
    return x
  }
  if (typeof x === 'number') {
    return x.toString()
  }
  return JSON.stringify(x)
}

/**
 * 分析軸の値のソートキーを組み立てる
 *
 * - `orderedValues` にある値は、一覧の順に並ぶ
 * - `orderedValues` に無い値は、一覧にある値すべてより後ろに並ぶ
 * - 一覧に無い値どうしは、表示名の昇順に並ぶ
 * - 一覧にある値どうしは一覧の順だけで決まるため、表示名を比べない
 *
 * @param orderedValues その分析軸に効いている並び順の一覧
 * @param dimensionValue 並べる対象の分析軸の値。ユーザー軸ならユーザー ID、通常の項目なら項目の値
 * @param label dimensionValue の表示名。orderedValues に無い値どうしの並び順に使う
 * @returns 順位, 同順位のときに比べる表示名, 内部値
 */
export const buildDimensionSortKey = (
  orderedValues: readonly string[],
  dimensionValue: string,
  label: string,
): readonly [number, string, string] => {
  const index = orderedValues.indexOf(dimensionValue)
  return index === -1 ? [orderedValues.length, label, dimensionValue] : [index, ``, dimensionValue]
}

/**
 * 表 (行・列) と並び替えメニューで並びを一致させるため、並び替えメニュー側もこの順序を使う。
 * 表示名だけロケールを `ja` に固定しているのは、既定ロケールではサーバーとブラウザで順序がずれるため
 * 内部値の比較は、並び順の指定がない分析軸の並びを変えないよう既存のままにしている
 */
export const dimensionSortKeyOrder = Order.Tuple([
  Order.Number,
  Order.make<string>((a, b) => String.localeCompare(b, [`ja`])(a)), // 表示名
  Order.make<string>((a, b) => String.localeCompare(b)(a)), // 内部値
])

/**
 * 合計と「-」を分析軸の値より外側に置くための順位。
 * 要素数が届かないであろう目安として 100000 にしている (既定の上限は 1000)
 */
const DIMENSION_FIXED_POSITION_UNIT = 100_000

/**
 * 行軸・列軸で共通のソートキーを組み立てる。
 * - 合計と「-」は分析軸の値ではないため、手動並び順の対象にせず分析軸の値より外側に固定する。
 */
const buildPivotDimensionSortKey = ({
  dimension,
  dimensionValue,
  label,
  isTotalPlacedFirst,
  resources,
}: {
  dimension: ViewQueryDimensionFieldForMultiTablePivot
  dimensionValue: string
  label: string
  isTotalPlacedFirst: boolean
  resources: DimensionSortResources
}): readonly [number, string, string] => {
  const fixedPositionSign = isTotalPlacedFirst ? -1 : 1
  // 合計・小計の行と列は、表示位置の設定に従って先頭または末尾に置く
  if (dimensionValue === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING) {
    return [fixedPositionSign * 2 * DIMENSION_FIXED_POSITION_UNIT, '', dimensionValue]
  }
  // 「-」(分析軸の値が空、またはユーザーとの紐付けに失敗したレコードのまとまり) は、合計・小計の表示位置に
  // 関係なく分析軸の値より後ろに置く
  if (dimensionValue === CORE_CONSTANT.KPI_PIVOT_NULL_STRING) {
    return [DIMENSION_FIXED_POSITION_UNIT, '', dimensionValue]
  }
  // KPI 軸は、ダッシュボードに設定された KPI の並び順で並べる
  if (dimension.fixedValues !== undefined && dimension.fixedValues.length > 0) {
    return buildDimensionSortKey(dimension.fixedValues, dimensionValue, label)
  }
  // 手動並び順が保存されている分析軸は、その並び順で並べる
  if (dimension.sortedValues !== undefined && dimension.sortedValues.length > 0) {
    return buildDimensionSortKey(dimension.sortedValues, dimensionValue, label)
  }
  // ユーザー軸は、ユーザー一覧の rank 順で並べる
  if (dimension.name === CORE_CONSTANT.KPI_PIVOT_USER_DIMENSION().key) {
    return buildDimensionSortKey(resources.userIds, dimensionValue, label)
  }
  // ユーザーグループ軸は、ユーザーグループ一覧の rank 順で並べる
  if (dimension.name.startsWith(`p_salescore_user_groups`)) {
    return buildDimensionSortKey(resources.userGroupIds, dimensionValue, label)
  }
  // 並び順の指定がない分析軸は、内部値の昇順で並べる
  return [0, ``, dimensionValue]
}

/**
 * 集計結果の 2 次元配列をクロス集計 (ピボット集計) し、結果を `RecordNode[]` の形に整形します。
 *
 * 本来クロス集計は 2 次元配列で表現するのが自然ですが、SALESCORE では後続の UI 処理で
 * `RSheet` (RecordNode を表示するシートコンポーネント) を流用したいという都合と、セル結合 /
 * 目標表示などのリッチな表示処理を扱いやすくしたいという理由から、ピボット集計の結果も
 * `RecordNode` 型に整形しています。
 *
 * ロジックの本体は「行の値でグルーピングして RecordNode を作る」を再帰的に行うだけです。
 * シートの `groupByCurrentNode` と概念的に同じ構造です。
 *
 * 葉ノードの `attributes` のキーは「列の値 + role」を `JSON.stringify` したものになり、`role`
 * には `actual` / `goal` が入って実績と目標を同じ RecordNode に持たせます。
 */
// eslint-disable-next-line @typescript-eslint/max-params
function groupByRowDimensions(
  sqlRows: SqlResultRecord[],
  pivot: ViewQueryMultiTablePivot['pivot'],
  sorter: ViewQueryMultiTablePivot['sorter'],
  options: ViewQueryMultiTablePivot['options'],
  depth: number,
  resources: DimensionSortResources,
): ViewQueryRecordNode[] {
  const [pivotRow] = pivot.rows
  if (pivotRow === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO
    return [
      {
        id: 'leaf', // なんでもいいはず
        attributes: columnsToAttributes(
          sqlRows,
          pivot.columns.map((x) => x.name),
        ),
        meta: {
          height: 1,
          innerRowIndexStart: 0, // 後で更新
          innerRowIndexEnd: 0, // 後で更新
        },
        children: [],
      },
    ]
  }

  // validation

  const sqlRowsGroups = Array.groupBy(sqlRows, (x) => {
    const dimension = x[pivotRow.name]
    if (typeof dimension === 'string') {
      return dimension
    }
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO

    return ``
  })

  return pipe(
    sqlRowsGroups,
    Record.toEntries,
    // 列軸の並び順は ui.pivotColumns の組み立てで決める
    // 集計値でのソートは、この後の sortByAggregatedValues で行う
    Array.sortBy(
      Order.mapInput(dimensionSortKeyOrder, ([dimensionValue, sqlRowsGroup]) =>
        buildPivotDimensionSortKey({
          dimension: pivotRow,
          dimensionValue,
          label: forceString(sqlRowsGroup[0][generateLabelSqlFieldName(pivotRow.name)]) ?? dimensionValue,
          isTotalPlacedFirst: sorter?.totalRowOrder === 'top',
          resources,
        }),
      ),
    ),
    Array.map(([dimensionValue, sqlRowsGroup]): ViewQueryRecordNode | undefined => {
      if ((options?.skipTotal ?? false) && dimensionValue === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING) {
        return undefined
      }
      const nextPivotRows = pivot.rows.slice(1)
      const [nextPivotRow] = nextPivotRows
      const children = [
        {
          nodeName: nextPivotRow === undefined ? `valuesNode` : `dimensionNode${depth}`,
          children: groupByRowDimensions(
            sqlRowsGroup,
            {
              ...pivot,
              rows: nextPivotRows,
            },
            sorter,
            options,
            depth + 1,
            resources,
          ),
        },
      ]
      const heights = children.map((x) => Number.sumAll(x.children.map((y) => y.meta.height)))
      return {
        id: dimensionValue, // uniqueになるはず
        attributes: {
          // eslint-disable-next-line sonarjs/todo-tag
          [createDimensionFieldName(depth)]: dimensionValue, // TODO
          [`${createDimensionFieldName(depth)}_label`]:
            dimensionValue === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING
              ? `小計`
              : sqlRowsGroup[0][generateLabelSqlFieldName(pivotRow.name)],
        },
        meta: {
          height: heights.length === 0 ? 1 : Math.max(...heights),
          innerRowIndexStart: 0, // 後で更新
          innerRowIndexEnd: 0, // 後で更新
        },
        children,
      }
    }),
    Array.filter(isNotNullish),
  )
}

//
//
//
function columnsToAttributes(sqlRows: SqlResultRecord[], pivotColumns: string[]): ViewQueryRecordNode['attributes'] {
  return pipe(
    sqlRows,
    Array.map((row) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/todo-tag
      const role = row[CORE_CONSTANT.KPI_PIVOT_ROLE_COLUMN_NAME] as string // TODO
      const attributeName = JSON.stringify([...pivotColumns.map((column) => row[column] ?? null), role])
      // eslint-disable-next-line sonarjs/todo-tag
      const value = row[CORE_CONSTANT.KPI_PIVOT_VALUE_COLUMN_NAME] // TODO
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/todo-tag
      return [attributeName, value] as [string, number] // TODO
    }),
    Record.fromEntries,
  )
}

export function createDimensionFieldName(depth: number) {
  // depthは1始まりとする
  return `${CORE_CONSTANT.DIMENSION_FIELD_NAME_PREFIX}${depth}` // XXX: このフォーマットに一部ロジックが依存している
}

async function fetchUsers(pivot: ViewQueryMultiTablePivot['pivot'], sqlClient: SqlClient) {
  const dimensions = [...pivot.rows, ...pivot.columns]
  const userDimension = dimensions.find((x) => x.name === CORE_CONSTANT.KPI_PIVOT_USER_DIMENSION().key)
  if (userDimension === undefined) {
    return []
  }
  const users = await sqlClient.query(`SELECT id, name, rank FROM salescore_users ORDER BY rank`)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return users.rows as unknown as UserOrUserGroup[]
}

async function fetchUserGroups(pivot: ViewQueryMultiTablePivot['pivot'], sqlClient: SqlClient) {
  const dimensions = [...pivot.rows, ...pivot.columns]
  // eslint-disable-next-line sonarjs/todo-tag
  const userGroupDimensions = dimensions.filter((x) => x.name.startsWith(`p_salescore_user_groups`)) // TODO
  if (userGroupDimensions.length === 0) {
    return []
  }
  const users = await sqlClient.query(`SELECT id, name, rank FROM salescore_user_groups ORDER BY rank`)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return users.rows as unknown as UserOrUserGroup[]
}

//
// ここまでのロジックだと子レコード数(? 正確にはflatNodeしたときのレコード数)が制限されていない。
// バックエンドのロジックとしては問題がないが、フロントエンドでレンダリングする際に問題になる？ような挙動があったため、
// いったん表示行数を絞る対応をしておく
//

function reduceRecordNode(
  records: ViewQueryRecordNode[],
  limit: number,
): {
  records: ViewQueryRecordNode[]
  reduced: boolean
} {
  const sumHeight = Number.sumAll(records.map((x) => x.meta.height))
  if (sumHeight <= limit) {
    return {
      records,
      reduced: false,
    }
  }

  let current = 0
  const result: ViewQueryRecordNode[] = []
  for (const record of records) {
    if (record.meta.height + current < limit) {
      result.push(record)
      current += record.meta.height
      continue
    }
    // heightが超えてしまう場合、超えないようなchildrenを求める
    const [tableNode] = record.children // ピボット集計のコンテキストでは、childrenは1つしかないはず
    if (tableNode === undefined) {
      return {
        reduced: true,
        records: result,
      }
    }
    const reducedChildren = reduceRecordNode(tableNode.children, limit - current)
    result.push({
      ...record,
      children: [
        {
          ...tableNode,
          children: reducedChildren.records,
        },
      ],
    })
    return {
      reduced: true,
      records: result,
    }
  }

  // ここに辿り着くことはないはず
  return {
    reduced: true,
    records: result,
  }
}
